<template>
  <div class="flex items-center justify-between gap-2">
    <template v-if="!showAsMenu">
      <Dropdown
        triggerStyle="p-2 hover:bg-[#F0F1F3] rounded-full"
        :close-on-outside-click="true"
        :closeOnSelect="true"
      >
        <template #trigger>
          <div class="relative">
            <EllipsisVerticalIcon class="text-[#686666] size-5" />
            <span
              v-if="
                can('can-resolve-comment') &&
                currentTab !== 'urgent_action_items' &&
                (order_item as DashboardItem).unresolved_comments_count > 0
              "
              class="absolute -top-4 -right-3 bg-[#ffae00] text-xs w-5 h-5 flex items-center justify-center rounded-full border border-white"
            >
              {{ (order_item as DashboardItem).unresolved_comments_count }}
            </span>
          </div>
        </template>
        <template #contents>
          <ActionMenuItems
            :order-item="order_item"
            :current-tab="currentTab"
            @select-row="emits('selectRow', $event)"
            @change-factory="(orderId, orderItemId) => changeFactory(orderId, orderItemId)"
            @view-pdf="(orderId) => getAwsURLFromOrder(orderId)"
            @open-provide-quote="provideQuoteModal = true"
            @notify-customer="(orderId) => notifyCustomerForQuote(orderId)"
            @delete-order="(orderId) => deleteOrder(orderId)"
            @price-breakdown="(orderItem) => priceBreakdown(orderItem)"
            @update-urgency="(type, id) => updateUrgency(type, id)"
            @fetch-pdf="(order, version) => fetchAndOpenPdf(order, version)"
            @upload-custom-techpack="(orderItem) => uploadCustomTechpack(orderItem)"
            @open-internal-note="internalNoteModal = true"
            @copy-order="(orderId) => copyOrder(orderId)"
          />
        </template>
      </Dropdown>
    </template>
    <template v-else-if="showAsMenu">
      <div class="bg-white rounded-lg shadow-md w-max sticky !z-[10000] !overflow-hidden" ref="menuRef">
        <ActionMenuItems
          :order-item="order_item"
          :current-tab="currentTab"
          @select-row="emits('selectRow', $event)"
          @change-factory="(orderId, orderItemId) => changeFactory(orderId, orderItemId)"
          @view-pdf="(orderId) => getAwsURLFromOrder(orderId)"
          @open-provide-quote="provideQuoteModal = true"
          @notify-customer="(orderId) => notifyCustomerForQuote(orderId)"
          @delete-order="(orderId) => deleteOrder(orderId)"
          @price-breakdown="(orderItem) => priceBreakdown(orderItem)"
          @update-urgency="(type, id) => updateUrgency(type, id)"
          @fetch-pdf="(order, version) => fetchAndOpenPdf(order, version)"
          @upload-custom-techpack="(orderItem) => uploadCustomTechpack(orderItem)"
          @open-internal-note="internalNoteModal = true"
          @copy-order="(orderId) => copyOrder(orderId)"
        />
      </div>
    </template>
    <Teleport to="body">
      <ProvideQuoteModal
        @onQuoteProvided="onFetch(true)"
        v-if="provideQuoteModal"
        :row="order_item"
      />
      <ChangeFactoryDialog
        :currentTab="currentTab"
        @close="onFetch"
        @closeMenu="forceCloseMenu"
        v-if="changeFactoryModal"
        :order_item="order_item"
      />

      <PriceBreakdownModal
        :order="order_ref"
        :appUrl="appURL"
        :order_item="order_item"
        v-if="priceBreakdownModal"
        @close="handlePriceBreakDownModalCloseEvent"
      />

      <Transition name="fade">
        <CustomTechPackModal
          v-if="customTechpackModal"
          :order="(order_item as DashboardItem).order"
          @close="handleCustomTechPackModalCloseEvent"
          @onFetch="onFetch"
        />
      </Transition>
    <Transition name="fade">
      <AddInternalNoteModal
        :order_item_id="(order_item as DashboardItem).id"
        :internal_comment="
          order_item.internal_comment ? JSON.parse(order_item.internal_comment) : null
        "
        v-if="internalNoteModal"
        @on-close="onFetch(true)"
        @on-cancel="
          () => {
            internalNoteModal = false;
            emits('closeMenu');
          }
        "
      />
    </Transition>
    <Transition name="fade">
      <CopyOrderModal
        :order="order_ref"
        v-if="openCopyModal"
        @close="() => (openCopyModal = false)"
        @save="onFetch"
      />
    </Transition>
    </Teleport>
  </div>
</template>
<script setup lang="ts">
import Dropdown from '@/components/Shared/Dropdown.vue';
import { can, logError, openV2Application } from '@/composables';
import { getAwsURLFromOrder } from '@/composables/index';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { DashboardItem, OrderDetail } from '@/models';
import { useMainStore } from '@/store';
import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid';
import { storeToRefs } from 'pinia';
import { onBeforeUnmount, onMounted, ref, Teleport } from 'vue';
import ActionMenuItems from './ActionMenuItems.vue';
import AddInternalNoteModal from './Modals/AddInternalNoteModal.vue';
import ChangeFactoryDialog from './Modals/ChangeFactoryModal.vue';
import CopyOrderModal from './Modals/CopyOrderModal.vue';
import CustomTechPackModal from './Modals/CustomTechPackModal.vue';
import PriceBreakdownModal from './Modals/PriceBreakdownModal.vue';
import ProvideQuoteModal from './Modals/ProvideQuoteModal.vue';

type Urgency = 'vip' | 'urgent' | 'extra_attention';

const storage_url = import.meta.env.VITE_APP_FILE_URL;

const changeFactoryModal = ref<boolean>(false);
const provideQuoteModal = ref<boolean>(false);
const priceBreakdownModal = ref<boolean>(false);
const customTechpackModal = ref<boolean>(false);
const internalNoteModal = ref<boolean>(false);
const openCopyModal = ref<boolean>(false);
const menuRef = ref<HTMLElement | null>(null);
// Use props for showAsMenu
// const props = defineProps<{ order_item_list: any; currentTab: string; showAsMenu: boolean }>();

const emits = defineEmits(['onFetch', 'selectRow', 'closeMenu']);
const { order_item_list, currentTab, showAsMenu } = defineProps<{
  order_item_list: any;
  currentTab: string;
  showAsMenu: boolean;
}>();

const { loading } = storeToRefs(useMainStore());
const appURL = import.meta.env.VITE_VUE2_APP_URL;
const order_item = ref({ ...order_item_list });
const order_ref = ref<OrderDetail>();
const {
  deleteOrder: DeleteFromStore,
  notifyCustomerForQuote,
  updateOrderUrgency,
  getOrderDetail,
} = useMainStore();

const updateUrgency = async (status: Urgency, orderId: number | string) => {
  await updateOrderUrgency(status, orderId);
  emits('selectRow', null);
  emits('onFetch', true);
};

const onFetch = (fetch: boolean) => {
  changeFactoryModal.value = false;
  provideQuoteModal.value = false;
  internalNoteModal.value = false;
  emits('selectRow', null);
  emits('onFetch', fetch);
  emits('closeMenu');
};
const deleteOrder = async (orderId: number): Promise<void> => {
  await DeleteFromStore(orderId);
  emits('selectRow', null);
  emits('onFetch', true);
};

const handlePriceBreakDownModalCloseEvent = (fetch_order_items = false) => {
  priceBreakdownModal.value = false;
  emits('selectRow', null);
  if (fetch_order_items) {
    emits('onFetch', fetch_order_items);
  }
};
// price list data get

const priceListData = async (orderId: number, orderItemId) => {
  try {
    order_ref.value = await getOrderDetail(orderId);
    let order_item_total_price = 0; // Use `let` instead of `const`
    const factory_products = order_ref.value.items?.[0]?.factory_products ?? [];
    factory_products.forEach((product) => {
      order_item_total_price += product?.prices?.factory_product_total_price ?? 0;
    });
    order_item.value.factory_products = factory_products;
    order_item.value.order_item_total_price = order_item_total_price;
  } catch (error) {
    logError('Error fetching price list data:', error);
    return null;
  }
};

const priceBreakdown = async (orderItem: DashboardItem) => {
  await priceListData(orderItem.order.id, orderItem.id);
  priceBreakdownModal.value = true;
};

const uploadCustomTechpack = async (orderItem: DashboardItem) => {
  customTechpackModal.value = true;
};
const handleCustomTechPackModalCloseEvent = () => {
  customTechpackModal.value = false;
};

const changeFactory = async (orderId: number, orderItemId: number) => {
  await priceListData(orderId, orderItemId);
  changeFactoryModal.value = true;
};

const fetchAndOpenPdf = async (order, version_id) => {
  try {
    let order_id = order.id;
    let techpack_data_length = order.techpack_data_count ? order.techpack_data_count : 0;
    loading.value = true;
    const response = await http.get(`/techpackpdf/order/${order_id}/version/${version_id}`);
    loading.value = false;
    const pdfUrl = response.data.result.pdf_url;
    if (pdfUrl) {
      window.open(`${storage_url}/${pdfUrl}`, '_blank');
    } else {
      if (techpack_data_length) {
        openV2Application(
          {
            name: '',
            to: `/order/${order_id}/condensend-pdf?techpack_version=${version_id}`,
          },
          true
        );
      } else {
        openV2Application({ name: '', to: `/order/${order_id}/condensend-pdf` }, true);
      }
    }
  } catch (error) {
    logError('Error fetching the PDF URL:', error);
  }
};

const copyOrder = async (orderId: number) => {
  try {
    order_ref.value = await getOrderDetail(orderId);
    openCopyModal.value = true;
  } catch (error) {
    logError('Error fetching the order details:', error);
  }
};

function handleOutsideClick(event: MouseEvent) {
  const menu = menuRef.value;
  const target = event.target as HTMLElement | null;
  if (!menu || !target) return;

  if (menu.contains(target)) return;

  if (
    target.closest(
      'input, select, option, textarea, .dropdown-menu, .dropdown-option, .urgency-dropdown, .modal-content, .vs__dropdown-option, label, .modal-backdrop'
    )
  ) {
    return;
  }

  if (showAsMenu) {
    emits('closeMenu');
  }
}
function forceCloseMenu() {
  if (showAsMenu) {
    emits('closeMenu');
  }
}
onMounted(() => {
  document.addEventListener('mousedown', handleOutsideClick);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleOutsideClick);
});
</script>
<style lang="scss" scoped></style>
